import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import type React from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Card,
    CardBody,
    CardHeadingIcon,
    CardLink,
    CardSurtitle,
    DialogRoot,
    DialogTrigger,
    Group,
    Heading,
    type IconDefinition,
    useDialogTriggerState,
} from '@gitbook/spine-react';

import { CreateSiteDialog, SpaceCreationDialog, useCreateInOwner } from '../../../../Components';
import type { NavigationTarget } from '../../../../navigation';
import { apiQuery } from '../../../../state';
import { useRecentlyEditedContent } from '../../hooks/useRecentlyEditedContent';
import { ContentCard } from './ContentCard';

/**
 * Top section of the home screen.
 *
 * We display the most recently edited spaces of the user (creators)
 * or of the organization (non-creators).
 */
export function JumpInSection({
    organization,
}: {
    organization: api.Organization;
}): React.ReactElement {
    const { createSpace } = useCreateInOwner({
        organization: organization.id,
    });
    const spaceCreationDialogTrigger = useDialogTriggerState();

    const { data: orgContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', { organizationId: organization.id })
    );

    const canCreate = organization.permissions.createContent;

    const mostRecentContent = useRecentlyEditedContent({
        organizationId: organization.id,
        orgContent,
    });

    return (
        <>
            <div className="flex flex-1 flex-col gap-6">
                <div className="flex flex-col">
                    <h1 className="text-ui-heading-large">
                        {!canCreate
                            ? 'Welcome'
                            : mostRecentContent.length > 0
                              ? 'Jump back in'
                              : `Let's get started`}
                    </h1>
                    <p className="mt-1 text-muted text-ui-base">
                        {!canCreate
                            ? mostRecentContent.length > 0
                                ? `Here are the most recently edited spaces in ${
                                      organization ? `${organization.title}` : 'your organization'
                                  }`
                                : ''
                            : mostRecentContent.length > 0
                              ? 'Your most recent content'
                              : 'Get to know GitBook by creating a new space or by importing existing knowledge'}
                    </p>
                </div>

                {mostRecentContent.length ? (
                    <ul
                        className={clsx(
                            'grid grid-cols-1 gap-6 lg:grid-cols-3',
                            mostRecentContent.length > 3 ? 'grid-rows-2' : 'grid-rows-1'
                        )}
                    >
                        {mostRecentContent.map((content) => (
                            <li
                                key={
                                    'site' in content.item
                                        ? content.item.site.id
                                        : content.item.space.id
                                }
                            >
                                <ContentCard content={content} organizationId={organization.id} />
                            </li>
                        ))}
                    </ul>
                ) : canCreate ? (
                    <ul className="grid grid-cols-1 grid-rows-1 gap-6 lg:grid-cols-3">
                        <li>
                            <DialogRoot>
                                <DialogTrigger>
                                    <ActionCard
                                        title="Create a docs site"
                                        surtitle="Start by creating your docs site"
                                        icon={Icon.DocsSite}
                                    />
                                </DialogTrigger>

                                <CreateSiteDialog organizationId={organization.id} />
                            </DialogRoot>
                        </li>

                        {createSpace ? (
                            <>
                                <li>
                                    <DialogTrigger trigger={spaceCreationDialogTrigger}>
                                        <ActionCard
                                            title="Create a space"
                                            surtitle="Start by creating your first space"
                                            icon={Icon.Space}
                                        />
                                    </DialogTrigger>
                                </li>

                                <DialogRoot {...spaceCreationDialogTrigger.rootProps}>
                                    <SpaceCreationDialog organizationId={organization.id} />
                                </DialogRoot>
                            </>
                        ) : null}

                        <li>
                            <ActionCardLink
                                title="Import content"
                                surtitle="Add from other sources"
                                icon={Icon.Download}
                                href={{
                                    overlay: {
                                        type: 'import',
                                        organization: organization.id,
                                    },
                                }}
                            />
                        </li>
                    </ul>
                ) : null}
            </div>
        </>
    );
}

function ActionCard({
    title,
    surtitle,
    icon,
}: {
    title: string;
    surtitle: string;
    icon: IconDefinition;
}) {
    return (
        <Card className="flex h-full">
            <CardBody>
                <CardSurtitle>{surtitle}</CardSurtitle>
                <Group>
                    <CardHeadingIcon>{icon}</CardHeadingIcon>
                    <Heading>{title}</Heading>
                </Group>
            </CardBody>
        </Card>
    );
}

function ActionCardLink({
    title,
    surtitle,
    icon,
    href,
}: {
    title: string;
    surtitle: string;
    icon: IconDefinition;
    href: NavigationTarget;
}) {
    return (
        <CardLink href={href} className="flex h-full">
            <CardBody>
                <CardSurtitle>{surtitle}</CardSurtitle>
                <Group>
                    <CardHeadingIcon>{icon}</CardHeadingIcon>
                    <Heading>{title}</Heading>
                </Group>
            </CardBody>
        </CardLink>
    );
}
